import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { verifyOTP, resendOTP } from '../services/api';

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: linear-gradient(135deg, #ff6b6b 0%, #ff8e53 100%);
  padding: 20px;
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.95);
  padding: 40px 30px;
  border-radius: 24px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 420px;
  text-align: center;
`;

const Title = styled.h1`
  font-size: 1.8rem;
  color: #333;
  margin-bottom: 10px;
`;

const Subtitle = styled.p`
  color: #666;
  margin-bottom: 30px;
  font-size: 1rem;
`;

const OTPContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-bottom: 25px;
`;

const OTPInput = styled.input`
  width: 45px;
  height: 45px;
  border: 2px solid #ddd;
  border-radius: 8px;
  text-align: center;
  font-size: 1.2rem;
  font-weight: bold;
  
  /* Remove number input spinners */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  &[type=number] {
    -moz-appearance: textfield;
  }
  
  &:focus {
    border-color: #ff6b6b;
    outline: none;
    box-shadow: 0 0 0 3px rgba(255, 107, 107, 0.1);
  }
`;

const Button = styled.button<{ secondary?: boolean }>`
  background: ${props => props.secondary ? 'transparent' : 'linear-gradient(135deg, #ff6b6b, #ff8e53)'};
  color: ${props => props.secondary ? '#ff6b6b' : 'white'};
  border: ${props => props.secondary ? '2px solid #ff6b6b' : 'none'};
  padding: 12px 24px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  margin-bottom: 10px;
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(255, 107, 107, 0.1);
  }
`;

const Timer = styled.div`
  color: #666;
  font-size: 0.9rem;
  margin-top: 10px;
`;

const LoadingIcon = styled(FontAwesomeIcon)`
  animation: spin 1s linear infinite;
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

interface LocationState {
    userId: string;
    email: string;
}

const OTPVerification: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { userId, email } = location.state as LocationState;

    const [otp, setOTP] = useState(['', '', '', '', '', '']);
    const [loading, setLoading] = useState(false);
    const [resendDisabled, setResendDisabled] = useState(true);
    const [timer, setTimer] = useState(30);
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    useEffect(() => {
        if (!userId || !email) {
            navigate('/register');
            return;
        }
        startResendTimer();
    }, [userId, email, navigate]);

    useEffect(() => {
        let interval: NodeJS.Timeout;
        if (timer > 0 && resendDisabled) {
            interval = setInterval(() => {
                setTimer((prev) => prev - 1);
            }, 1000);
        } else if (timer === 0) {
            setResendDisabled(false);
        }
        return () => clearInterval(interval);
    }, [timer, resendDisabled]);

    const startResendTimer = () => {
        setResendDisabled(true);
        setTimer(30);
    };

    const handleChange = (index: number, value: string) => {
        // Only allow numbers
        if (!/^\d*$/.test(value)) return;

        if (value.length > 1) return;

        const newOTP = [...otp];
        newOTP[index] = value;
        setOTP(newOTP);

        // Move to next input
        if (value !== '' && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handleVerify = async () => {
        const otpString = otp.join('');
        if (otpString.length !== 6) {
            toast.error('Please enter complete OTP');
            return;
        }

        setLoading(true);
        try {
            await verifyOTP({
                userId,
                email,
                otp: parseInt(otpString)
            });
            toast.success('Email verified successfully!');
            navigate('/login');
        } catch (error) {
            toast.error(error instanceof Error ? error.message : 'Verification failed');
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        try {
            await resendOTP({ userId, email });
            toast.success('New OTP has been sent to your email');
            startResendTimer();
            setOTP(['', '', '', '', '', '']);
            inputRefs.current[0]?.focus();
        } catch (error) {
            toast.error(error instanceof Error ? error.message : 'Failed to resend OTP');
        }
    };

    // Add ref callback function
    const setRef = (index: number) => (el: HTMLInputElement | null) => {
        inputRefs.current[index] = el;
    };

    const handlePaste = (e: React.ClipboardEvent, currentIndex: number) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').trim();

        // Check if pasted content contains only numbers
        if (!/^\d+$/.test(pastedData)) {
            toast.error('Please paste numbers only');
            return;
        }

        // Take only first 6 digits if pasted content is longer
        const digits = pastedData.slice(0, 6).split('');

        // Create new OTP array with pasted digits
        const newOTP = [...otp];
        digits.forEach((digit, index) => {
            if (currentIndex + index < 6) {
                newOTP[currentIndex + index] = digit;
            }
        });

        setOTP(newOTP);

        // Focus on the next empty input or the last input if all are filled
        const nextEmptyIndex = newOTP.findIndex((digit, index) =>
            index >= currentIndex && digit === ''
        );

        if (nextEmptyIndex !== -1) {
            inputRefs.current[nextEmptyIndex]?.focus();
        } else {
            inputRefs.current[5]?.focus();
        }
    };

    return (
        <Container>
            <Card>
                <Title>Verify Your Email</Title>
                <Subtitle>
                    We've sent a 6-digit verification code to<br />
                    <strong>{email}</strong>
                </Subtitle>

                <OTPContainer>
                    {otp.map((digit, index) => (
                        <OTPInput
                            key={index}
                            ref={setRef(index)}
                            type="number"
                            inputMode="numeric"
                            pattern="\d*"
                            maxLength={1}
                            value={digit}
                            onChange={(e) => handleChange(index, e.target.value)}
                            onKeyDown={(e) => handleKeyDown(index, e)}
                            onPaste={(e) => handlePaste(e, index)}
                            autoFocus={index === 0}
                        />
                    ))}
                </OTPContainer>

                <Button
                    onClick={handleVerify}
                    disabled={loading || otp.join('').length !== 6}
                >
                    {loading ? <LoadingIcon icon={faSpinner} /> : 'Verify Email'}
                </Button>

                <Button
                    secondary
                    onClick={handleResend}
                    disabled={resendDisabled}
                >
                    Resend OTP
                </Button>

                {resendDisabled && (
                    <Timer>
                        Resend available in {timer} seconds
                    </Timer>
                )}
            </Card>
        </Container>
    );
};

export default OTPVerification; 